import { useMutation, useQuery } from "@tanstack/react-query";
import type { LoginInput, LoginResponse, Portal, SessionUser } from "@wakaskul/shared";
import { primaryPortal } from "@wakaskul/shared";

import { api } from "./api";
import { useAuthStore } from "./auth-store";
import { clearPersistedCache } from "./query-client";

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);

  return useMutation({
    mutationFn: (input: LoginInput) =>
      api<LoginResponse>("/auth/login", { method: "POST", body: input }),
    onSuccess: (data) => setSession(data.accessToken, data.user),
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);

  return useMutation({
    mutationFn: () => api<void>("/auth/logout", { method: "POST" }),
    // Clear locally regardless — if the request failed the token is expiring
    // within fifteen minutes anyway, and leaving a shared staffroom machine
    // signed in is the worse outcome.
    onSettled: async () => {
      clear();
      await clearPersistedCache();
    },
  });
}

export function useMe() {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["me"],
    queryFn: () => api<SessionUser>("/auth/me"),
    enabled: Boolean(token),
  });
}

/**
 * Route prefix per portal. The student portal lives at /me rather than
 * /student because the URL is read by the person it belongs to.
 */
export const PORTAL_PATH: Readonly<Record<Portal, string>> = {
  admin: "/admin",
  teach: "/teach",
  parent: "/parent",
  student: "/me",
};

/** Where a signed-in user should land. */
export function landingPath(user: SessionUser | null): string {
  if (!user) return "/login";

  // Platform staff belong to no school — that is expected for them, not a
  // broken session. Let them start in the complete school-system preview,
  // which has the same sidebar and workflows they will manage for a tenant.
  // Tenant provisioning remains available from that sidebar.
  if (user.isPlatformStaff) return "/demo";

  // A session with no school is not a pending choice any more — login
  // resolves the school from the subdomain up front — so there is nothing
  // to send this user to but a fresh login.
  if (!user.activeSchoolId) return "/login";

  const portal: Portal | null = primaryPortal(user.roles);
  return portal ? PORTAL_PATH[portal] : "/no-access";
}
